import i18n from '@/utils/i18n';
import React from 'react';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import {
  LOCATION_MANAGER_ROUTES,
  ORDER_GUIDE_ROUTES,
} from '@/navigators/routes';
import { LocationManagerStackParams } from '@/navigators/types';
import { MULTI_ORDER_GUIDE_QUERY } from '@/contexts/items-context-queries';
import {
  OrderGuide_CreateFromMenuMutation,
  OrderGuide_CreateFromMenuMutationVariables,
} from '@/types';
import { StackScreenProps } from '@react-navigation/stack';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useGetDynamicConfigValueForChat } from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';
import { ViewMenuModal } from '@/components/locations/view-menu-modal';

export const CREATE_ORDER_GUIDE_FROM_MENU_MUTATION = gql`
  mutation OrderGuide_CreateFromMenu(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    createOrderGuideFromMenu(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      order_guide_collection_uuid
      order_guide_partition_uuid
    }
  }
`;

type Props = StackScreenProps<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.ViewAccountMenu
>;

export const ViewAccountMenuScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { menu, restaurantName, restaurantUUID, supplierUUID, chatUUID } =
    route.params;
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();
  const { showErrorToast } = useToasts();

  const { selectRestaurant } = useAuthenticatedUserContext();

  // use same gating logic as in ViewAccountScreen
  const showMenuOrderGuideGeneration = chatUUID
    ? getDynamicConfigValueForChat(
        chatUUID,
        'menu_og_generation_enabled',
        false,
      )
    : false;

  const [createOrderGuide, { loading }] = useMutation<
    OrderGuide_CreateFromMenuMutation,
    OrderGuide_CreateFromMenuMutationVariables
  >(CREATE_ORDER_GUIDE_FROM_MENU_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(MULTI_ORDER_GUIDE_QUERY) as string],
  });

  const onGenerateOrderGuide = async () => {
    try {
      await createOrderGuide({
        variables: {
          restaurantUUID,
          supplierUUID,
        },
      });
      selectRestaurant(restaurantUUID);
      navigation.popToTop();
      navigation.goBack();
      // @ts-ignore
      navigation.navigate(ORDER_GUIDE_ROUTES.Base, {});
    } catch (e) {
      showErrorToast();
    }
  };

  return (
    <ViewMenuModal
      generateOrderGuideLoading={loading}
      menuItems={menu}
      noMenuItemsMessage={i18n.t('missing_menu_message')}
      noMenuItemsTitle={i18n.t('missing_menu_title')}
      onGenerateOrderGuide={onGenerateOrderGuide}
      restaurantName={restaurantName}
      restaurantUUID={restaurantUUID}
      showGenerateOrderGuideButton={showMenuOrderGuideGeneration ?? false}
    />
  );
};
